import { useState, useEffect } from 'react';
import axios from 'axios';
import { WinCheckbox } from './WinComponents';
import DateInput from './DateInput';
import AvocatAutocomplete from './AvocatAutocomplete';
import { formatMontant } from '../utils/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Onglet Décision — réplique stricte du legacy VB.NET FrmDaj (TabDecision).
 *
 * Structure :
 *   • Sub-tab "Acceptation" (TabAdmis)  → critères d'admissibilité positifs
 *   • Sub-tab "Refus"       (TabRefus)  → motifs de refus (art. 70 Loi AJ)
 *   • Footer (Panel8) toujours visible : Admis / Attestation / Refusé +
 *     signature avocat + dossier confié + Avocat/Notaire
 *
 * Mapping daj legacy (cf. CodeDaj.vb:496-568 RempFin / 2093-2280 ToDaj) :
 *   adda600 = Gratuite               adda602 = Avec contribution
 *   adda604 = Atteinte liberté       adda606 = Sécurité phys
 *   adda608 = Sécurité psy           adda610 = Moyen subsistance
 *   adda612 = Besoins essentiels     adda614 = Difficulté/conséq.
 *   adda620 = Serv. nommément couv.  adda622 = Emprisonnement
 *   adda624 = Perte subsistance      adda626 = Intérêt justice
 *   adda628 = Recours extra          IntVeritable = Intérêt véritable
 *   adda640-669 = motifs de refus (cf. modèle backend)
 *   adda654 = ADMIS                  adda670 = REFUSÉ
 *   adda671 = Attestation cond.      adda675 = Refus contribuer
 *   adda700 = Date sig.              adda702/704 = Avocat signataire
 *   adda706/712 = Dossier confié     adda708 = "A"/"N"
 */
// Sous-composant autocomplete pour la sélection d'un avocat.
// Comportement :
//   - Input libre : l'utilisateur tape le nom (ou une partie) ou le code
//   - Liste déroulante en dessous : filtre live sur code + label (nom, prénom)
//   - Clic sur une entrée → set code + nom simultanément
//   - Blur (perte de focus) → si input == code exact d'un avocat, garde le
//     nom associé ; sinon restaure le nom actuellement stocké
const AvocatAutocompleteLocal_REMOVED = () => null;  // remplacé par import partagé (2026-02-15)

const SectionHeader = ({ children }) => (
  <div
    style={{
      fontWeight: 700,
      fontSize: '13px',
      color: 'var(--primary-blue)',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      padding: '10px 4px 4px 4px',
      borderBottom: '1px solid #c0c8d4',
      marginBottom: 2,
    }}
  >
    {children}
  </div>
);

// Checkbox avec label + référence légale (style legacy)
const LegalCheckbox = ({ field, label, article, testId, disabled = false, decision, updateDecision }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto auto',
      gap: 12,
      alignItems: 'center',
      padding: '4px 6px',
    }}
    data-testid={testId ? `${testId}-row` : undefined}
  >
    <span style={{ fontSize: '15px', whiteSpace: 'pre-line' }}>{label}</span>
    <span style={{ fontSize: '13px', color: '#555' }}>
      {article}
    </span>
    <input
      type="checkbox"
      checked={decision[field] || false}
      onChange={e => updateDecision(field, e.target.checked)}
      disabled={disabled}
      style={{ width: 16, height: 16, accentColor: 'var(--primary-blue)', cursor: 'pointer' }}
      data-testid={testId}
    />
  </div>
);

const DecisionTab = ({ formData, updateDecision }) => {
  const [subTab, setSubTab] = useState('acceptation');
  const [avocatsSign, setAvocatsSign] = useState([]);   // Signature : code A*
  const [avocatsAll, setAvocatsAll] = useState([]);     // Dossier confié : tous non-T*
  const [calcSommaire, setCalcSommaire] = useState(null); // Sommaire financier LIVE
  const decision = formData.decision || {};

  // Sommaire financier LIVE : recalculé à partir des revenus/actifs COURANTS
  // (sans écraser les cases de décision de l'utilisateur). Corrige l'incohérence
  // où le sommaire restait figé sur un ancien calcul après modification des revenus.
  const revenusJSON = JSON.stringify(formData.revenus || {});
  const actifsJSON = JSON.stringify(formData.actifs || {});
  useEffect(() => {
    let annule = false;
    const params = new URLSearchParams({
      categorie: formData.categorie_requerant || '01',
      date_demande: formData.date_demande || '',
      region_eloignee: String(!!formData.region_eloignee),
      reciprocite: String(!!formData.reciprocite),
    });
    axios.post(`${API}/calcul-admissibilite?${params.toString()}`, {
      revenus: formData.revenus || {},
      actifs: formData.actifs || {},
    }).then(res => { if (!annule) setCalcSommaire(res.data); })
      .catch(() => { if (!annule) setCalcSommaire(null); });
    return () => { annule = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenusJSON, actifsJSON, formData.categorie_requerant, formData.date_demande, formData.region_eloignee, formData.reciprocite]);

  // === Cases Avocat / Notaire — indicateurs EN LECTURE seule ===
  // Représentent la 1re lettre du code « Dossier confié à » : P = Avocat, N = Notaire.
  const confieCode = (decision.dossier_confie_code || '').trim().toUpperCase();
  const chkAvocat = confieCode.startsWith('P') || confieCode.startsWith('A');
  const chkNotaire = confieCode.startsWith('N');

  // « Dossier confié à » : recherche dans tous les avocats (aucun filtre).
  const avocatsFiltres = avocatsAll;

  // Chargement en parallèle des 2 listes :
  //   - Signature avocat  → code A* uniquement (avocats plaidants)
  //   - Dossier confié    → tous les avocats (hors techniciens T*)
  useEffect(() => {
    axios.get(`${API}/reference/avocats?prefix=A`)
      .then(res => setAvocatsSign(res.data || []))
      .catch(e => console.error('Erreur chargement avocats A*', e));
    axios.get(`${API}/reference/avocats`)
      .then(res => setAvocatsAll(res.data || []))
      .catch(e => console.error('Erreur chargement avocats', e));
  }, []);

  // Handler générique : met à jour code + nom en un seul appel.
  const handleAvocatChange = (codeField, nomField, newCode, listeSource) => {
    const av = listeSource.find(a => a.code === (newCode || '').trim().toUpperCase());
    updateDecision(codeField, (newCode || '').trim().toUpperCase());
    updateDecision(nomField, av ? av.label : '');
  };

  // === Mutual exclusion legacy (Main.vb:773 ClickDecision) ===
  // Les 5 cases de décision FINALE (Admis / Attestation / Refusé / Refus
  // consultation / Refus contribuer) sont mutuellement exclusives : cocher
  // l'une décoche les 4 autres. AUCUNE n'est jamais désactivée (le greffier
  // peut toujours basculer la décision). Les MOTIFS de refus (sous-onglet
  // Refus, ex. « Inadmissibilité financière ») sont indépendants et ne
  // verrouillent pas la décision finale.
  const FINALS = ['admissible', 'attestation_conditionnelle', 'inadmissible_financier', 'refus_consultation', 'refus_contribuer'];

  const setDecisionSmart = (field, value) => {
    // Choix OUVERT : aucune décision n'est calculée/forcée automatiquement.
    // Seule règle conservée : les 4 décisions finales sont mutuellement
    // exclusives (1 seul choix possible). Cocher l'une décoche les autres ;
    // on peut aussi tout décocher (aucune décision).
    if (value === true && FINALS.includes(field)) {
      FINALS.forEach(f => { if (f !== field) updateDecision(f, false); });
      updateDecision(field, true);
      return;
    }
    // Gratuit / avec_contribution restent exclusifs entre eux (admissibilité
    // financière), mais n'influencent PLUS la décision finale.
    if (value === true && field === 'gratuit') {
      updateDecision('avec_contribution', false);
      updateDecision('gratuit', true);
      return;
    }
    if (value === true && field === 'avec_contribution') {
      updateDecision('gratuit', false);
      updateDecision('avec_contribution', true);
      return;
    }
    updateDecision(field, value);
  };

  // Wrapper qui injecte les valeurs de décision + le setter (réduit la
  // verbosité des appels — chaque LegalCheckbox aurait sinon 5+ props).
  // eslint-disable-next-line react/no-unstable-nested-components
  const LC = (props) => (
    <LegalCheckbox {...props} decision={decision} updateDecision={setDecisionSmart} />
  );

  // ============================================================
  // SUB-TAB : ACCEPTATION
  // ============================================================
  const renderAcceptation = () => (
    <fieldset className="win-groupbox compact" style={{ marginTop: 0, marginBottom: 0 }} data-testid="decision-acceptation-content">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* === COLONNE GAUCHE === */}
        <div>
          {/* Admissibilité financière */}
          <div style={{ marginBottom: 8 }}>
            <SectionHeader>Admissibilité financière :</SectionHeader>
            <div style={{ display: 'flex', gap: 24, padding: '8px 8px' }}>
              <label className="win-checkbox">
                <span style={{ fontSize: 15 }}>Gratuite</span>
                <input
                  type="checkbox"
                  checked={decision.gratuit || false}
                  onChange={e => setDecisionSmart('gratuit', e.target.checked)}
                  data-testid="chk-gratuit"
                />
              </label>
              <label className="win-checkbox">
                <span style={{ fontSize: 15 }}>Avec contribution</span>
                <input
                  type="checkbox"
                  checked={decision.avec_contribution || false}
                  onChange={e => setDecisionSmart('avec_contribution', e.target.checked)}
                  data-testid="chk-avec-contribution"
                />
              </label>
            </div>
          </div>

          {/* Couverture discrétionnaire (civil) */}
          <SectionHeader>Couverture discrétionnaire (civil) :</SectionHeader>
          <LC
            field="atteinte_liberte"
            label="Atteinte grave à la liberté"
            article="Art.4.7(8)"
            testId="chk-atteinte-liberte"
          />

          <SectionHeader>Met en cause :</SectionHeader>
          <LC
            field="securite_phys"
            label="Sécurité physique"
            article="Art.4.7(4) / Art.4.7(9)"
            testId="chk-securite-phys"
          />
          <LC
            field="securite_psy"
            label="Sécurité psychologique"
            article="Art.4.7(4) / Art.4.7(9)"
            testId="chk-securite-psy"
          />
          <LC
            field="moyen_subsistance"
            label="Moyen de subsistance"
            article="Art.4.7(9)"
            testId="chk-moyen-subsistance"
          />
          <LC
            field="besoins_essentiels"
            label="Besoins essentiels"
            article="Art.4.7(9)"
            testId="chk-besoins-essentiels"
          />

          <SectionHeader>Rédaction d&apos;un document :</SectionHeader>
          <LC
            field="difficulte_consequences"
            label="Difficulté et conséquences"
            article="Art.4.10(3)"
            testId="chk-difficulte-consequences"
          />
        </div>

        {/* === COLONNE DROITE === */}
        <div>
          <div style={{ marginBottom: 8 }}>
            <SectionHeader>&nbsp;</SectionHeader>
            <LC
              field="serv_nommement_couvert"
              label="Service nommément couvert"
              article=""
              testId="chk-serv-nommement-couvert"
            />
          </div>

          <SectionHeader>Couverture discrétionnaire (criminel) :</SectionHeader>
          <LC
            field="emprisonnement_garde"
            label="Emprisonnement/mise sous garde"
            article="Art.4.5(3) / Art.4.9"
            testId="chk-emprisonnement-garde"
          />
          <LC
            field="perte_subsistance"
            label="Perte de moyen de subsistance"
            article="Art.4.5(3) / Art.4.9"
            testId="chk-perte-subsistance"
          />
          <LC
            field="interet_justice"
            label="Intérêt de la justice"
            article="Art.4.5(3) / Art.4.9"
            testId="chk-interet-justice"
          />
          <LC
            field="interet_veritable"
            label="Intérêt véritable"
            article="Art.4.5(3) / Art.4.9"
            testId="chk-interet-veritable"
          />
          <LC
            field="recours_extraordinaire"
            label={"Appel ou recours extraordinaire en demande\n(motifs en apparence sérieux)"}
            article="Art. 4.6(2)"
            testId="chk-recours-extraordinaire"
          />
        </div>
      </div>
    </fieldset>
  );

  // ============================================================
  // SUB-TAB : REFUS  (Réplique STRICTE du legacy VB.NET TabRefus)
  // Layout : 2 colonnes, chacune divisée en 2 sections :
  //   Gauche haut  : cases libres (Inadmissibilité financière, Service nommément exclu)
  //   Gauche bas   : cadre "Application de l'article 4.11" (6 cases)
  //   Droite haut  : cases libres (Article 69, Service non couvert, Appel/recours 4.6(2), À l'encontre de la loi)
  //   Droite bas   : cadre "Application de l'article 70" (4 cases)
  // ============================================================
  // Titre de section souligné (parité `lblArt411` / `lblArt70` legacy)
  // eslint-disable-next-line react/no-unstable-nested-components
  const RefusSectionTitle = ({ children }) => (
    <div
      style={{
        fontWeight: 700,
        fontSize: '13px',
        color: '#7a1f1a',
        textDecoration: 'underline',
        padding: '6px 6px 6px 6px',
      }}
    >
      {children}
    </div>
  );

  const renderRefus = () => (
    <fieldset
      className="win-groupbox compact"
      style={{ marginTop: 0, marginBottom: 0, borderColor: '#c0625c', background: '#fdf3f2' }}
      data-testid="decision-refus-content"
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* === COLONNE GAUCHE === */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Section haut gauche (cases libres, sans encadré) */}
          <div style={{ padding: '4px 0' }}>
            <LC
              field="refus_inadmis_financier"
              label="Inadmissibilité financière"
              article=""
              testId="chk-refus-inadmis-financier"
            />
            <LC
              field="refus_serv_nomm_exclu"
              label="Service nommément exclu"
              article=""
              testId="chk-refus-serv-nomm-exclu"
            />
          </div>

          {/* Section bas gauche : Application de l'article 4.11 (cadre) */}
          <div style={{ background: '#fff', padding: '4px 8px 8px 8px', border: '1px solid #c0625c', borderRadius: 6 }}>
            <RefusSectionTitle>Application de l&apos;article 4.11</RefusSectionTitle>
            <LC
              field="refus_non_vraisemblable"
              label="Non-vraisemblance de droit"
              article="Art. 4.11(1)"
              testId="chk-refus-non-vraisemblable"
            />
            <LC
              field="refus_peu_succes"
              label="Peu de chance de succès"
              article="Art. 4.11(2)"
              testId="chk-refus-peu-succes"
            />
            <LC
              field="refus_cout_deraisonnable"
              label="Coût déraisonnable"
              article="Art. 4.11(3)"
              testId="chk-refus-cout-deraisonnable"
            />
            <LC
              field="refus_imposs_exec"
              label="Impossibilité d'exécution"
              article="Art. 4.11(4)"
              testId="chk-refus-imposs-exec"
            />
            <LC
              field="refus_regl_raisonnable"
              label="Refus règlement raisonnable"
              article="Art. 4.11(5)"
              testId="chk-refus-regl-raisonnable"
            />
            <LC
              field="refus_autre_serv_dispo"
              label="Autres services disponibles"
              article=""
              testId="chk-refus-autre-serv-dispo"
            />
          </div>
        </div>

        {/* === COLONNE DROITE === */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Section haut droite (cases libres) */}
          <div style={{ padding: '4px 0' }}>
            <LC
              field="refus_art_69"
              label="Application de l'article 69"
              article=""
              testId="chk-refus-art-69"
            />
            <LC
              field="refus_serv_non_couvert"
              label="Service non couvert"
              article=""
              testId="chk-refus-serv-non-couvert"
            />
            <LC
              field="refus_appel_non_fonde"
              label={"Appel ou recours extraordinaire en demande\n(absence d'apparence de motifs sérieux)"}
              article="Art. 4.6(2)"
              testId="chk-refus-appel-non-fonde"
            />
            <LC
              field="refus_encontre_loi"
              label="À l'encontre de la loi sur l'aide juridique"
              article=""
              testId="chk-refus-encontre-loi"
            />
          </div>

          {/* Section bas droite : Application de l'article 70 (cadre) */}
          <div style={{ background: '#fff', padding: '4px 8px 8px 8px', border: '1px solid #c0625c', borderRadius: 6 }}>
            <RefusSectionTitle>Application de l&apos;article 70</RefusSectionTitle>
            <LC
              field="refus_disposition_bien"
              label="Disposition d'un bien"
              article=""
              testId="chk-refus-disposition-bien"
            />
            <LC
              field="refus_renseignements"
              label="Refus de fournir des renseignements"
              article=""
              testId="chk-refus-renseignements"
            />
            <LC
              field="refus_renseignements_faux"
              label="Renseignement faux ou inexacts"
              article=""
              testId="chk-refus-renseignements-faux"
            />
            <LC
              field="refus_autre_cas_70"
              label="Autres cas de l'article 70"
              article=""
              testId="chk-refus-autre-cas-70"
            />
          </div>
        </div>
      </div>
    </fieldset>
  );

  // ============================================================
  // FOOTER (Panel8) — toujours visible
  // ============================================================
  const renderFooter = () => (
    <fieldset className="win-groupbox compact" style={{ marginTop: 0, marginBottom: 0, flex: 1, width: '100%' }}>
      <legend>Décision finale</legend>
      {/* Checkboxes finales — 2 colonnes : L1 = Admis / Refusé,
          L2 = Attestation conditionnelle / Refus de contribuer. MAJUSCULES. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', padding: '8px', marginBottom: 10 }}>
        <label className="win-checkbox" style={{ fontWeight: 700, fontSize: 14, textTransform: 'uppercase' }}>
          <input
            type="checkbox"
            checked={decision.admissible || false}
            onChange={e => setDecisionSmart('admissible', e.target.checked)}
            data-testid="chk-admis"
          />
          <span>Admis</span>
        </label>
        <label className="win-checkbox" style={{ fontWeight: 700, fontSize: 14, textTransform: 'uppercase', color: '#7a1f1a' }}>
          <input
            type="checkbox"
            checked={decision.inadmissible_financier || false}
            onChange={e => setDecisionSmart('inadmissible_financier', e.target.checked)}
            style={{ accentColor: '#c0625c' }}
            data-testid="chk-refuse"
          />
          <span>Refusé</span>
        </label>
        <label className="win-checkbox" style={{ fontWeight: 700, fontSize: 14, textTransform: 'uppercase' }}>
          <input
            type="checkbox"
            checked={decision.attestation_conditionnelle || false}
            onChange={e => setDecisionSmart('attestation_conditionnelle', e.target.checked)}
            data-testid="chk-attestation-conditionnelle"
          />
          <span>Attestation conditionnelle</span>
        </label>
        <label className="win-checkbox" style={{ fontWeight: 700, fontSize: 14, textTransform: 'uppercase' }}>
          <input
            type="checkbox"
            checked={decision.refus_contribuer || false}
            onChange={e => setDecisionSmart('refus_contribuer', e.target.checked)}
            data-testid="chk-refus-contribuer"
          />
          <span>Refus de contribuer</span>
        </label>
      </div>

      {/* Signature avocat + Dossier confié + Avocat/Notaire */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '0 8px',
        }}
      >
        <div className="win-field" style={{ width: 150, flexShrink: 0 }}>
          <label className="win-field-label">Date</label>
          <DateInput
            value={decision.date_signature || ''}
            onChange={v => updateDecision('date_signature', v)}
            minDate={formData.date_demande || undefined}
            minLabel="date de la demande"
            testId="input-date-signature-decision"
          />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
          <div className="win-field" style={{ flex: 1, minWidth: 0 }}>
            <label className="win-field-label">Signature de l&apos;avocat</label>
            <AvocatAutocomplete
              liste={avocatsSign}
              code={decision.signature_avocat_code || ''}
              nom={decision.signature_avocat_nom || ''}
              onSelect={a => {
                updateDecision('signature_avocat_code', a.code);
                updateDecision('signature_avocat_nom', a.label);
              }}
              onClear={() => {
                updateDecision('signature_avocat_code', '');
                updateDecision('signature_avocat_nom', '');
              }}
              testId="autocomplete-signature-avocat"
              listFontSize={14}
            />
          </div>
          <div className="win-field" style={{ width: 90, flexShrink: 0 }}>
            <label className="win-field-label">No avocat</label>
            <input
              className="win-input"
              value={decision.signature_avocat_code || ''}
              readOnly
              tabIndex={-1}
              style={{ textAlign: 'center', fontWeight: 600, background: '#eef0f3', cursor: 'default' }}
              data-testid="input-signature-avocat-code"
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
          <div className="win-field" style={{ flex: 1, minWidth: 0 }}>
            <label className="win-field-label">Dossier confié à</label>
            <AvocatAutocomplete
              liste={avocatsFiltres}
              code={decision.dossier_confie_code || ''}
              nom={decision.dossier_confie_nom || ''}
              onSelect={a => {
                updateDecision('dossier_confie_code', a.code);
                updateDecision('dossier_confie_nom', a.label);
              }}
              onClear={() => {
                updateDecision('dossier_confie_code', '');
                updateDecision('dossier_confie_nom', '');
              }}
              testId="autocomplete-dossier-confie"
              listFontSize={14}
            />
          </div>
          <div className="win-field" style={{ width: 90, flexShrink: 0 }}>
            <label className="win-field-label">No avocat</label>
            <input
              className="win-input"
              value={decision.dossier_confie_code || ''}
              readOnly
              tabIndex={-1}
              style={{ textAlign: 'center', fontWeight: 600, background: '#eef0f3', cursor: 'default' }}
              data-testid="input-dossier-confie-code"
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', paddingBottom: 4 }}>
          <label className="win-checkbox"
                 title="Indicateur (lecture) : code confié débutant par P">
            <input
              type="checkbox"
              checked={chkAvocat}
              readOnly
              style={{ pointerEvents: 'none', cursor: 'default' }}
              data-testid="chk-type-avocat"
            />
            <span style={{ fontSize: 15 }}>Avocat</span>
          </label>
          <label className="win-checkbox"
                 title="Indicateur (lecture) : code confié débutant par N">
            <input
              type="checkbox"
              checked={chkNotaire}
              readOnly
              style={{ pointerEvents: 'none', cursor: 'default' }}
              data-testid="chk-type-notaire"
            />
            <span style={{ fontSize: 15 }}>Notaire</span>
          </label>
        </div>
      </div>
    </fieldset>
  );

  // ============================================================
  // Sommaire financier (récap calc — conservé sous le formulaire)
  // ============================================================
  const renderSommaire = () => {
    const s = calcSommaire || decision;
    if (!s || (!s.message && !s.grand_total_revenus && !s.total_revenus_req)) return null;
    return (
      <fieldset className="win-groupbox" style={{ marginTop: 0, flex: 1, width: '100%' }}>
        <legend>Sommaire financier</legend>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6, fontSize: '13px' }}>
          <div>Revenus requérant : <strong>{formatMontant(s.total_revenus_req)} $</strong></div>
          <div>Revenus famille : <strong>{formatMontant(s.total_revenus_fam)} $</strong></div>
          <div>Déductions : <strong>{formatMontant(s.total_deductions)} $</strong></div>
          <div>Grand total revenus : <strong>{formatMontant(s.grand_total_revenus)} $</strong></div>
          <div>Total biens : <strong>{formatMontant(s.total_biens)} $</strong></div>
          <div>Total liquidités : <strong>{formatMontant(s.total_liquidites)} $</strong></div>
          <div>Seuil catégorie : <strong>{formatMontant(s.seuil_categorie)} $</strong>{formData.region_eloignee && <span style={{ marginLeft: 6, color: '#b26a00', fontStyle: 'italic' }} data-testid="seuil-region-eloignee">(seuil pour région éloignée)</span>}</div>
          {s.contribution_max > 0 && (
            <div>Contribution max : <strong>{formatMontant(s.contribution_max)} $</strong> (+ {formatMontant(s.frais_admin)} $ frais admin)</div>
          )}
        </div>
        {s.message && (
          <div style={{ marginTop: 6, padding: 6, background: '#fff8d0', borderLeft: '3px solid #c8a000', fontSize: '13px' }}>
            {s.message}
          </div>
        )}
      </fieldset>
    );
  };

  return (
    <div className="win-tab-content compact" data-testid="decision-tab">
      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
        {/* GAUCHE : Décision finale (placée à gauche pour réduire la largeur
            de la section Acceptation/Refus → cases à cocher plus proches du texte) */}
        <div style={{ flex: '0 0 580px', maxWidth: 580, display: 'flex' }}>
          {renderFooter()}
        </div>

        {/* DROITE : Acceptation / Refus */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Sub-tabs */}
          <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid var(--primary-blue)' }}>
            <button
              type="button"
              onClick={() => setSubTab('acceptation')}
              className={`win-btn ${subTab === 'acceptation' ? 'active' : ''}`}
              style={{
                background: subTab === 'acceptation' ? 'var(--primary-blue)' : '#e4e9f0',
                color: subTab === 'acceptation' ? '#fff' : 'var(--primary-blue)',
                border: '1px solid var(--primary-blue)',
                borderBottom: 'none',
                borderRadius: '6px 6px 0 0',
                padding: '6px 20px',
                fontWeight: 700,
                fontSize: '13px',
              }}
              data-testid="tab-decision-acceptation"
            >
              Acceptation
            </button>
            <button
              type="button"
              onClick={() => setSubTab('refus')}
              className={`win-btn ${subTab === 'refus' ? 'active' : ''}`}
              style={{
                background: subTab === 'refus' ? '#f4c7c3' : '#e4e9f0',
                color: subTab === 'refus' ? '#7a1f1a' : 'var(--primary-blue)',
                border: subTab === 'refus' ? '1px solid #c0625c' : '1px solid var(--primary-blue)',
                borderBottom: 'none',
                borderRadius: '6px 6px 0 0',
                padding: '6px 20px',
                fontWeight: 700,
                fontSize: '13px',
              }}
              data-testid="tab-decision-refus"
            >
              Refus
            </button>
          </div>

          {subTab === 'acceptation' ? renderAcceptation() : renderRefus()}
        </div>

        {/* DROITE : Sommaire financier en colonne */}
        <div style={{ flex: '0 0 240px', maxWidth: 240, display: 'flex', flexDirection: 'column' }} data-testid="sommaire-financier-col">
          {/* Espaceur : aligne le haut du cadre avec le contenu Acceptation/Refus
              (compense la barre d'onglets Acceptation/Refus). */}
          <div style={{ height: 34, flexShrink: 0 }} aria-hidden="true" />
          {renderSommaire()}
        </div>
      </div>
    </div>
  );
};

export default DecisionTab;
